/**
 * Frecuencia de muestreo y alcanzabilidad de los detectores.
 *
 * Nace del ADR-007. El Pipeline V5 está calibrado para telemetría de alta
 * frecuencia; la flota reporta con intervalo mediano de ~10 s. A esa cadencia,
 * el umbral de 2,5 m/s² exige variar 90 km/h entre dos puntos, y ningún
 * vehículo lo hace. El motor entonces devuelve cero episodios.
 *
 * Un cero mudo se lee como «maneja perfecto». Este módulo existe para que el
 * informe pueda decir la verdad: «no medimos», y por qué.
 *
 * **La alcanzabilidad se evalúa contra el perfil que se va a aplicar**, no
 * contra uno fijo. Desde que existe la familia por cadencia (`profiles.ts`),
 * dar por sentado `LAB_PROFILE` producía una contradicción en pantalla: el
 * aviso decía que las aceleraciones eran inalcanzables mientras el análisis
 * reportaba mil episodios de aceleración, porque cada uno hablaba de umbrales
 * distintos. El aviso tiene que describir el análisis que se hizo.
 *
 * No modifica el análisis. Solo lo describe.
 */

import type { GuardiaHistoryRecord } from "../contracts/telemetry.ts";
import { parseStrictIsoTimestamp } from "./analysis-period.ts";
import { regimeForCadence } from "./cadence-regime.ts";
import { LAB_PROFILE } from "./lab-profile.ts";
import type { AnalysisProfile } from "./profiles.ts";

export type DetectorReachability = {
  detector: "acceleration" | "braking" | "turn" | "elevated_speed";
  label: string;
  /** Qué tendría que pasar entre dos puntos, a esta cadencia, para dispararlo. */
  requirement: string;
  /** `false` cuando lo exigido es físicamente imposible para un vehículo. */
  reachable: boolean;
  /**
   * `true` cuando la cadencia pasa del techo de las maniobras
   * ({@link MANEUVER_CADENCE_CEILING_SECONDS}). Es un `reachable: false` más
   * fuerte: lo que el detector marque a esa cadencia no es una maniobra, y por
   * eso ni siquiera un episodio encontrado prueba que la familia se midió.
   */
  beyondCadenceCeiling?: boolean;
};

export type SamplingQuality = {
  samples: number;
  intervals: number;
  /**
   * Cadencia EN MARCHA (ADR-011): mediana de los intervalos entre dos lecturas
   * consecutivas que van las dos con velocidad. `null` si el período no trae
   * {@link MINIMUM_MOVING_INTERVALS} intervalos así. Media, mínimo y máximo se
   * miden sobre los mismos intervalos; `samples`, `intervals` y los duplicados,
   * sobre todos los puntos.
   */
  medianIntervalSeconds: number | null;
  meanIntervalSeconds: number | null;
  minIntervalSeconds: number | null;
  maxIntervalSeconds: number | null;
  /** Intervalos de cero segundos: dos lecturas para el mismo instante. */
  duplicateInstants: number;
  duplicateInstantPercent: number | null;
  /**
   * `HIGH` < 3 s · `MEDIUM` 3 a menos de 7 s · `LOW` 7 s o más; `UNKNOWN` sin
   * cadencia en marcha. Es el régimen del perfil que `selectProfile` aplica a
   * {@link medianIntervalSeconds}: la frontera vive en `cadence-regime.ts`.
   */
  regime: "HIGH" | "MEDIUM" | "LOW" | "UNKNOWN";
  detectors: DetectorReachability[];
  /** Verdadero cuando algún detector quedó fuera de alcance por la cadencia. */
  detectorsBlindedBySampling: boolean;
  explanation: string | null;
};

/**
 * Techo de cadencia para medir maniobras, en segundos.
 *
 * Aceleración, frenada y giro se miden entre los centros de dos tramos. Con un
 * punto cada 30 s o más, una maniobra entera —arrancar, frenar, doblar una
 * esquina— cabe dentro de un solo intervalo: lo que queda es la diferencia entre
 * dos velocidades o dos rumbos sueltos separados medio minuto, y eso mide el
 * tráfico, no la maniobra. Es también donde termina el perfil de 10 s
 * (`appliesToIntervalSeconds: [7, 30]`): por encima no había ningún perfil y el
 * código caía en silencio al de 10 s.
 *
 * La velocidad sostenida no tiene techo: se suma tramo a tramo.
 */
export const MANEUVER_CADENCE_CEILING_SECONDS = 30;

/**
 * Intervalos en marcha que hacen falta para hablar de la cadencia de un equipo.
 * Con menos —un minuto y medio de marcha a 4 s, tres a 10 s— la mediana es de
 * una muestra demasiado chica y el período tampoco da para medir maniobras.
 */
export const MINIMUM_MOVING_INTERVALS = 20;

/** Un vehículo de calle no sostiene más de ~11 m/s² (frenada de pánico). */
const PHYSICAL_ACCELERATION_CEILING_MPS2 = 11;
/** Ni gira más de ~90° en un segundo a velocidad de circulación. */
const PHYSICAL_YAW_CEILING_DEGREES_PER_SECOND = 90;

function median(values: number[]): number | null {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[middle]! : (sorted[middle - 1]! + sorted[middle]!) / 2;
}

const round = (value: number) => Math.round(value * 100) / 100;

const isMoving = (record: GuardiaHistoryRecord) =>
  typeof record.speed_kmh === "number" && Number.isFinite(record.speed_kmh) && record.speed_kmh > 0;

function describeDetectors(
  intervalSeconds: number,
  profile: AnalysisProfile,
): DetectorReachability[] {
  const { longitudinal, turns, elevatedSpeed } = profile;

  // Δv necesaria (km/h) = umbral (m/s²) × intervalo (s) × 3,6
  const accelerationDeltaKmh = longitudinal.accelerationThresholdMps2 * intervalSeconds * 3.6;
  const brakingDeltaKmh = Math.abs(longitudinal.brakingThresholdMps2) * intervalSeconds * 3.6;
  const headingDegrees = turns.minimumYawRateDegreesPerSecond * intervalSeconds;

  // Por encima del techo, las tres familias de maniobra quedan fuera sin importar
  // el umbral: no hay perilla que convierta medio minuto en una maniobra.
  if (intervalSeconds >= MANEUVER_CADENCE_CEILING_SECONDS) {
    const beyond = (detector: "acceleration" | "braking" | "turn", label: string): DetectorReachability => ({
      detector,
      label,
      requirement:
        `con un punto cada ${round(intervalSeconds)} s una maniobra cabe entera entre dos lecturas; ` +
        `el techo para medir maniobras es ${MANEUVER_CADENCE_CEILING_SECONDS} s`,
      reachable: false,
      beyondCadenceCeiling: true,
    });
    return [
      beyond("acceleration", "Aceleraciones"),
      beyond("braking", "Frenadas"),
      beyond("turn", "Giros"),
      elevatedSpeedReachability(intervalSeconds, elevatedSpeed),
    ];
  }

  return [
    {
      detector: "acceleration",
      label: "Aceleraciones",
      requirement: `variar ${round(accelerationDeltaKmh)} km/h en ${round(intervalSeconds)} s`,
      reachable:
        longitudinal.accelerationThresholdMps2 <= PHYSICAL_ACCELERATION_CEILING_MPS2 &&
        accelerationDeltaKmh <= PHYSICAL_ACCELERATION_CEILING_MPS2 * intervalSeconds * 3.6 &&
        accelerationDeltaKmh <= 60,
    },
    {
      detector: "braking",
      label: "Frenadas",
      requirement: `variar ${round(brakingDeltaKmh)} km/h en ${round(intervalSeconds)} s`,
      reachable: brakingDeltaKmh <= 60,
    },
    {
      detector: "turn",
      label: "Giros",
      requirement: `girar ${round(headingDegrees)}° en ${round(intervalSeconds)} s`,
      reachable:
        headingDegrees < 180 &&
        headingDegrees <= PHYSICAL_YAW_CEILING_DEGREES_PER_SECOND * intervalSeconds,
    },
    elevatedSpeedReachability(intervalSeconds, elevatedSpeed),
  ];
}

function elevatedSpeedReachability(
  intervalSeconds: number,
  elevatedSpeed: AnalysisProfile["elevatedSpeed"],
): DetectorReachability {
  return {
    detector: "elevated_speed",
    label: "Velocidad elevada técnica",
    // No depende de la cadencia: basta con sostener la velocidad en un tramo.
    requirement:
      elevatedSpeed.sustainedWindowSeconds > 0
        ? `sostener ${elevatedSpeed.operationalSpeedLimitKmh} km/h durante ` +
          `${elevatedSpeed.sustainedWindowSeconds} s, sumando tramos`
        : `sostener ${elevatedSpeed.operationalSpeedLimitKmh} km/h durante ${elevatedSpeed.minimumSegmentDurationSeconds} s en un solo tramo`,
    // Con la ventana encendida la cadencia deja de ser una barrera: si hace
    // falta, se suman tramos hasta reunirla. Con la regla vieja, en cambio,
    // reportar MÁS seguido que la duración pedida la vuelve imposible — y
    // por eso el alcance dependía de la cadencia al revés de lo razonable.
    reachable:
      elevatedSpeed.sustainedWindowSeconds > 0
        ? true
        : intervalSeconds >= elevatedSpeed.minimumSegmentDurationSeconds,
  };
}

/**
 * Solo la cadencia: cuántos puntos, cada cuánto, cuántos duplicados.
 *
 * Se separa del resto porque es lo único que se puede saber **antes** de
 * escoger el perfil — y el perfil se escoge justamente con esto. Primero se
 * mide cómo reporta el equipo, después se decide con qué umbrales juzgarlo.
 */
export type CadenceMeasurement = Pick<
  SamplingQuality,
  | "samples"
  | "intervals"
  | "medianIntervalSeconds"
  | "meanIntervalSeconds"
  | "minIntervalSeconds"
  | "maxIntervalSeconds"
  | "duplicateInstants"
  | "duplicateInstantPercent"
  | "regime"
>;

/**
 * Cada cuánto reporta el equipo CON EL VEHÍCULO EN MARCHA (ADR-011).
 *
 * ## El error que esto corrige
 *
 * Antes se tomaba la mediana de todos los intervalos del período, y en un mes
 * un vehículo pasa la mayor parte del tiempo estacionado. Hay equipos que
 * estacionados reportan cada minuto y en marcha cada pocos segundos, y en
 * ellos la mediana salía de los puntos estacionados.
 *
 * Se midió el 06/10/2026, punto a punto, en un equipo gt06:
 * - con todos los puntos, la mediana daba 63 s;
 * - en marcha reportaba cada 4 s.
 *
 * Drive lo trataba como un equipo de más de 30 s, le aplicaba el perfil de 10 s
 * y, desde el ADR-009, lo dejaba sin medición de maniobras por el techo. Las
 * maniobras ocurren en marcha; la resolución que importa es la de la marcha.
 *
 * ## Cómo se mide
 *
 * Un intervalo cuenta si las dos lecturas que lo cierran van con velocidad
 * mayor que 0 y ninguna es una posición descartada. El punto que repite la
 * fijación anterior con velocidad 0 en plena marcha (ADR-010) queda fuera solo:
 * su velocidad es 0.
 */
export function measureCadence(records: GuardiaHistoryRecord[]): CadenceMeasurement {
  const readings = records
    .map((record) => ({ time: parseStrictIsoTimestamp(record.timestamp), record }))
    .filter((reading): reading is { time: number; record: GuardiaHistoryRecord } => reading.time !== null)
    .sort((a, b) => a.time - b.time);

  const gaps: number[] = [];
  for (let i = 1; i < readings.length; i++) gaps.push((readings[i]!.time - readings[i - 1]!.time) / 1000);

  const duplicateInstants = gaps.filter((gap) => gap === 0).length;

  // Los duplicados no dicen nada de la cadencia, y las posiciones descartadas
  // no entran al cálculo: ninguno de los dos cierra un intervalo en marcha.
  const usable = readings.filter((reading) => reading.record.validation_status !== "DISCARDED");
  const moving: number[] = [];
  for (let i = 1; i < usable.length; i++) {
    const before = usable[i - 1]!;
    const after = usable[i]!;
    const gap = (after.time - before.time) / 1000;
    if (gap > 0 && isMoving(before.record) && isMoving(after.record)) moving.push(gap);
  }
  const positive = moving.length >= MINIMUM_MOVING_INTERVALS ? moving : [];

  const medianInterval = median(positive);
  const meanInterval = positive.length
    ? positive.reduce((total, gap) => total + gap, 0) / positive.length
    : null;
  const publishedMedian = medianInterval === null ? null : round(medianInterval);

  // El régimen se decide con la cifra publicada, que es la que recibe
  // `selectProfile`. Con la mediana sin redondear, 6,996 s sería MEDIUM aquí y
  // LOW en el perfil: otra frontera doble, de milésimas.
  const regime: SamplingQuality["regime"] =
    publishedMedian === null ? "UNKNOWN" : regimeForCadence(publishedMedian);

  return {
    samples: readings.length,
    intervals: gaps.length,
    medianIntervalSeconds: publishedMedian,
    meanIntervalSeconds: meanInterval === null ? null : round(meanInterval),
    minIntervalSeconds: positive.length ? round(Math.min(...positive)) : null,
    maxIntervalSeconds: positive.length ? round(Math.max(...positive)) : null,
    duplicateInstants,
    duplicateInstantPercent: gaps.length ? round((duplicateInstants / gaps.length) * 100) : null,
    regime,
  };
}

/**
 * Cadencia + qué alcanza a detectar el perfil aplicado a esa cadencia.
 *
 * El perfil es un parámetro y no un supuesto: es lo que hace que el aviso
 * describa el análisis que de verdad se corrió.
 */
export function describeSampling(
  cadence: CadenceMeasurement,
  profile: AnalysisProfile = LAB_PROFILE as AnalysisProfile,
): SamplingQuality {
  const medianInterval = cadence.medianIntervalSeconds;
  const detectors = medianInterval === null ? [] : describeDetectors(medianInterval, profile);
  const blinded = detectors.some((detector) => !detector.reachable);

  const beyondCeiling = detectors.some((detector) => detector.beyondCadenceCeiling);

  const explanation =
    medianInterval === null || !blinded
      ? null
      : beyondCeiling
        ? `En marcha, el equipo reporta un punto cada ${round(medianInterval)} s, por encima de los ` +
          `${MANEUVER_CADENCE_CEILING_SECONDS} s del techo para medir maniobras: una aceleración, una ` +
          `frenada o un giro caben enteros entre dos lecturas. Esas tres familias no se pueden medir con ` +
          `este equipo con ningún perfil —tampoco con ${profile.profileId}—, y lo que el motor marque en ` +
          `ellas no cuenta como medición.`
        : `En marcha, el equipo reporta un punto cada ${round(medianInterval)} s. Con los umbrales del perfil ` +
          `${profile.profileId}, a esa cadencia ` +
          `${detectors
            .filter((detector) => !detector.reachable)
            .map((detector) => detector.label.toLowerCase())
            .join(", ")} quedan fuera de alcance. Un cero en esas familias significa ` +
          `«no se pudo medir», no «no ocurrió».`;

  return { ...cadence, detectors, detectorsBlindedBySampling: blinded, explanation };
}

/** Atajo para quien no necesita separar los dos pasos. */
export function assessSamplingQuality(
  records: GuardiaHistoryRecord[],
  profile: AnalysisProfile = LAB_PROFILE as AnalysisProfile,
): SamplingQuality {
  return describeSampling(measureCadence(records), profile);
}
